/**
 * Painel do mês, a tela principal: números da equipe e os gráficos
 * 1 (% da meta), 2 (participação no faturamento) e 3 (carteira atendida, só na
 * Aliança), cada um em barras ou na corrida dos carrinhos. Clicar num
 * representante abre a tela dele.
 * Dois painéis: Aliança (marcas do Winthor, itens 1, 2 e 3 do pedido) e
 * Bauny (SAP, itens 1.1, 2.1 e 3.1). A troca fica no topo.
 *
 * Lê: dados do mês (dados/usar-dados-do-mes.ts) e os cadastros.
 * Monta os números com: dominio/painel.ts. Arruma para os gráficos com: telas/montar-graficos.ts.
 */
import { useMemo, useState } from 'react';
import { CartaoGrafico } from '../componentes/CartaoGrafico.tsx';
import { Aviso, ErroNaTela, Indicador, SeletorMes, TrocaDePainel } from '../componentes/basicos.tsx';
import { usarDadosDoMes } from '../dados/usar-dados-do-mes.ts';
import { fechamentoEspecial } from '../dominio/fechamento.ts';
import { juntarFeriados } from '../dominio/feriados.ts';
import { montarPainel, type PainelMes } from '../dominio/painel.ts';
import type { Cadastros, Fonte, Mes } from '../dominio/tipos.ts';
import {
  formatarData,
  formatarDataCurta,
  formatarDataHora,
  formatarMoeda,
  formatarMoedaInteira,
  formatarPorcentagem,
  listar,
  NOME_DO_PAINEL,
  NOME_DO_SISTEMA,
  nomeCurtoDoMes,
  nomeDoMes,
} from '../formatacao.ts';
import { CORES_CLARO, usarCores, type Cores } from '../graficos/cores.ts';
import { CorridaCarrinhos } from '../graficos/CorridaCarrinhos.tsx';
import { LARGURA_IMAGEM } from '../graficos/exportar.ts';
import { GraficoBarras } from '../graficos/GraficoBarras.tsx';
import type { Cabecalho } from '../graficos/Moldura.tsx';
import { irPara, linkPara } from '../rotas.ts';
import { escalaAjustada, escalaDaMeta, itensDaCorrida, itensDeBarras, notasDoGrafico, periodoDosDados, tabelaDoGrafico, type Medida } from './montar-graficos.ts';

type Visao = 'barras' | 'corrida';
const VISOES = [
  { id: 'corrida', rotulo: 'Corrida' },
  { id: 'barras', rotulo: 'Barras' },
];

interface Props {
  mes: Mes;
  /** winthor = painel da Aliança; sap-bauny = painel da Bauny. */
  fonte: Fonte;
  cadastros: Cadastros;
}

function desenhar(painel: PainelMes, medida: Medida, visao: Visao, largura: number, cores: Cores, cabecalho?: Cabecalho) {
  const abrir = (id: number) => irPara({ tela: 'representante', id, mes: painel.mes, fonte: painel.fonte });
  const descricao = {
    atingimento: 'Meta atingida por representante',
    participacao: 'Participação de cada representante no faturamento',
    carteira: 'Carteira de clientes atendida por representante',
  }[medida];
  const ritmo = medida === 'atingimento' ? painel.ritmo : null;
  if (visao === 'corrida') {
    const itens = itensDaCorrida(painel, medida, abrir);
    const escala = escalaAjustada(itens.map((i) => i.valor), ritmo);
    return (
      <CorridaCarrinhos
        itens={itens}
        maximo={escala.maximo}
        marcasEixo={escala.marcas}
        formatarMarca={(v) => formatarPorcentagem(v, 0)}
        marcador={ritmo === null ? undefined : { valor: ritmo, rotulo: `RITMO HOJE ${formatarPorcentagem(ritmo, 0)}` }}
        largura={largura}
        cores={cores}
        descricao={descricao}
        cabecalho={cabecalho}
      />
    );
  }
  if (medida === 'atingimento') {
    const escala = escalaDaMeta(painel);
    return (
      <GraficoBarras
        itens={itensDeBarras(painel, medida, abrir)}
        maximo={escala.maximo}
        marcasEixo={escala.marcas}
        formatarMarca={(v) => formatarPorcentagem(v, 0)}
        trilhoAte={1}
        marcadores={escala.marcadores}
        largura={largura}
        cores={cores}
        descricao={descricao}
        cabecalho={cabecalho}
      />
    );
  }
  const itens = itensDeBarras(painel, medida, abrir);
  const escala = escalaAjustada(itens.map((i) => i.valor), null);
  return (
    <GraficoBarras
      itens={itens}
      maximo={escala.maximo}
      marcasEixo={escala.marcas}
      formatarMarca={(v) => formatarPorcentagem(v, 0)}
      valorNaPonta
      largura={largura}
      cores={cores}
      descricao={descricao}
      cabecalho={cabecalho}
    />
  );
}

export function Painel({ mes, fonte, cadastros }: Props) {
  const cores = usarCores();
  const { dados, carregando, erro } = usarDadosDoMes(mes, fonte);
  // Em tela estreita (celular) a corrida fica apertada: começa em barras. A imagem para WhatsApp sai sempre em 1080 px.
  const [visaoMeta, setVisaoMeta] = useState<Visao>(() => (window.innerWidth < 640 ? 'barras' : 'corrida'));
  const [visaoParticipacao, setVisaoParticipacao] = useState<Visao>('barras');
  const [visaoCarteira, setVisaoCarteira] = useState<Visao>('barras');

  const painel = useMemo(
    () =>
      dados
        ? montarPainel({ mes: dados.mes, fonte, vendas: dados.vendas, cadastros, metas: dados.metas, feriados: juntarFeriados(cadastros.feriados), carteiras: dados.carteiras })
        : null,
    [dados, cadastros, fonte],
  );

  function cabecalho(titulo: string, subtitulo: string, medida: Medida): Cabecalho | undefined {
    if (!painel) return undefined;
    return {
      titulo,
      subtitulo,
      rodape: notasDoGrafico(painel, medida),
      assinatura: `Metas Aliança · gerado em ${formatarDataHora(new Date().toLocaleString('sv-SE'))}`,
      logo: cadastros.logo ?? null,
    };
  }

  const semVendas = painel !== null && painel.dataReferencia === null;
  // Sem meta nas marcas DESTE painel (ex.: a Bauny pode estar sem meta mesmo com as do Winthor lançadas).
  const semMetas = painel !== null && painel.equipe.meta === 0;
  const nomePainel = NOME_DO_PAINEL[fonte];
  const sistema = NOME_DO_SISTEMA[fonte];
  const mesCurto = `${nomePainel} · ${nomeCurtoDoMes(mes)}`;
  const prefixoArquivo = fonte === 'sap-bauny' ? 'bauny-' : '';
  const sufixoArquivo = `${prefixoArquivo}${painel?.dataReferencia ? `${mes}-ate-${formatarDataCurta(painel.dataReferencia).replace('/', '-')}` : mes}`;
  const participacaoDeQue = fonte === 'winthor' ? 'sem Bauny (ela tem o painel dela)' : 'só Bauny';

  return (
    <div className="pagina">
      <div className="pagina-titulo">
        <div>
          <h1>Painel de vendas · {nomePainel}</h1>
          <p>
            {sistema} ·{' '}
            {painel?.dataReferencia
              ? `dados até ${formatarData(painel.dataReferencia)} (${painel.dias.passados} de ${painel.dias.total} dias úteis)`
              : nomeDoMes(mes)}
          </p>
        </div>
        <SeletorMes mes={mes} aoMudar={(novo) => irPara({ tela: 'painel', mes: novo, fonte })} />
      </div>
      <TrocaDePainel ativo={fonte} link={(outra) => linkPara({ tela: 'painel', mes, fonte: outra })} />

      {erro ? <ErroNaTela erro={erro} /> : null}
      {semVendas ? (
        <Aviso tipo="info">
          Nenhuma venda do {sistema} importada para {nomeDoMes(mes)}. <a href={linkPara({ tela: 'importar' })}>Importar arquivo</a>
        </Aviso>
      ) : null}
      {semMetas ? (
        <Aviso tipo="alerta">
          Não há metas {fonte === 'sap-bauny' ? 'da Bauny' : 'das marcas do Winthor'} lançadas para {nomeDoMes(mes)}: o % da meta fica vazio.{' '}
          <a href={linkPara({ tela: 'metas', mes })}>Lançar metas</a>
        </Aviso>
      ) : null}
      {painel && fechamentoEspecial(fonte, mes) ? (
        <Aviso tipo="info">
          {nomePainel} em {nomeDoMes(mes)}: o mês vai de {formatarData(painel.periodo.inicio)} a {formatarData(painel.periodo.fim)} (fechamento combinado com a
          gerência).
        </Aviso>
      ) : null}
      {painel && painel.origem.marcasSemArquivo.length > 0 ? (
        <Aviso tipo="alerta">
          Falta importar o arquivo de {listar(painel.origem.marcasSemArquivo.map((m) => m.nome))} em {nomeDoMes(mes)}: sem ele, o realizado dessa marca fica
          zerado. <a href={linkPara({ tela: 'importar' })}>Importar</a>
        </Aviso>
      ) : null}
      {painel && new Set(painel.origem.marcas.map((m) => m.ultimaData)).size > 1 ? (
        <Aviso tipo="info">
          Os arquivos das marcas vão até datas diferentes ({painel.origem.marcas.map((m) => `${m.marca.nome} até ${formatarDataCurta(m.ultimaData)}`).join(', ')}). O
          ritmo usa a data mais recente; importe todas as marcas no mesmo dia para comparar certo.
        </Aviso>
      ) : null}
      {painel && painel.naoIdentificados.length > 0 ? (
        <Aviso tipo="alerta">
          {painel.naoIdentificados.length === 1 ? 'Há 1 vendedor sem cadastro' : `Há ${painel.naoIdentificados.length} vendedores sem cadastro`}:{' '}
          {painel.naoIdentificados.map((v) => `código ${v.codigo} (${v.nome}, ${formatarMoeda(v.liquido)})`).join('; ')}. Essas vendas ficam fora da
          conta até o cadastro. <a href={linkPara({ tela: 'cadastros', mes })}>Cadastrar agora</a>
        </Aviso>
      ) : null}

      {painel ? (
        <div className={carregando ? 'pilha carregando' : 'pilha'}>
          <div className="indicadores">
            <Indicador
              principal
              rotulo="Faturamento líquido da equipe"
              valor={formatarMoeda(painel.equipe.liquido)}
              detalhe={`${painel.equipe.notas} notas · ${painel.equipe.clientes} clientes${
                painel.equipe.carteira === null ? '' : ` (${formatarPorcentagem(painel.equipe.carteira)} da carteira)`
              } · devoluções ${formatarMoeda(painel.equipe.devolucao)}`}
            />
            <Indicador
              rotulo="Meta da equipe atingida"
              valor={formatarPorcentagem(painel.equipe.atingimento)}
              detalhe={painel.equipe.meta > 0 ? `de ${formatarMoedaInteira(painel.equipe.meta)} · falta ${formatarMoedaInteira(painel.equipe.faltaParaMeta)}` : 'sem metas no mês'}
            />
            <Indicador
              rotulo="Ritmo esperado hoje"
              valor={formatarPorcentagem(painel.ritmo, 0)}
              detalhe={`${painel.dias.passados} de ${painel.dias.total} dias úteis`}
            />
            <Indicador
              rotulo="Projeção do mês"
              valor={painel.equipe.projecao === null ? '—' : formatarMoedaInteira(painel.equipe.projecao)}
              detalhe={painel.equipe.projecao === null ? 'sem vendas ainda' : `${formatarPorcentagem(painel.equipe.atingimentoProjetado)} da meta no ritmo atual`}
            />
          </div>

          <CartaoGrafico
            titulo="Meta atingida por representante"
            subtitulo={`Faturamento ${periodoDosDados(painel)} ÷ meta de ${nomeDoMes(mes)} · equipe em ${formatarPorcentagem(painel.equipe.atingimento)} da meta`}
            visoes={VISOES}
            visao={visaoMeta}
            aoTrocarVisao={(id) => setVisaoMeta(id as Visao)}
            tabela={tabelaDoGrafico(painel, 'atingimento')}
            notas={notasDoGrafico(painel, 'atingimento')}
            desenhar={(largura) => desenhar(painel, 'atingimento', visaoMeta, largura, cores)}
            paraImagem={() =>
              desenhar(
                painel,
                'atingimento',
                visaoMeta,
                LARGURA_IMAGEM,
                CORES_CLARO,
                cabecalho(
                  visaoMeta === 'corrida' ? `Corrida da meta · ${mesCurto}` : `Meta atingida por representante · ${mesCurto}`,
                  `Faturamento ${periodoDosDados(painel)} sobre a meta do mês · equipe em ${formatarPorcentagem(painel.equipe.atingimento)} da meta`,
                  'atingimento',
                ),
              )
            }
            nomeImagem={`meta-${sufixoArquivo}.png`}
          />

          <CartaoGrafico
            titulo="Participação no faturamento"
            subtitulo={`Quanto cada representante representa do faturamento líquido da equipe ${periodoDosDados(painel)} · ${participacaoDeQue}`}
            visoes={VISOES}
            visao={visaoParticipacao}
            aoTrocarVisao={(id) => setVisaoParticipacao(id as Visao)}
            tabela={tabelaDoGrafico(painel, 'participacao')}
            notas={notasDoGrafico(painel, 'participacao')}
            desenhar={(largura) => desenhar(painel, 'participacao', visaoParticipacao, largura, cores)}
            paraImagem={() =>
              desenhar(
                painel,
                'participacao',
                visaoParticipacao,
                LARGURA_IMAGEM,
                CORES_CLARO,
                cabecalho(
                  visaoParticipacao === 'corrida' ? `Corrida do faturamento · ${mesCurto}` : `Participação no faturamento · ${mesCurto}`,
                  `Participação de cada representante no faturamento líquido ${periodoDosDados(painel)} · ${participacaoDeQue}`,
                  'participacao',
                ),
              )
            }
            nomeImagem={`participacao-${sufixoArquivo}.png`}
          />

          {painel.carteiraUsada ? (
            <CartaoGrafico
              titulo="Carteira atendida no mês"
              subtitulo={`Clientes que compraram ${periodoDosDados(painel)} ÷ clientes da carteira de cada representante · equipe em ${formatarPorcentagem(painel.equipe.carteira)}`}
              visoes={VISOES}
              visao={visaoCarteira}
              aoTrocarVisao={(id) => setVisaoCarteira(id as Visao)}
              tabela={tabelaDoGrafico(painel, 'carteira')}
              notas={notasDoGrafico(painel, 'carteira')}
              desenhar={(largura) => desenhar(painel, 'carteira', visaoCarteira, largura, cores)}
              paraImagem={() =>
                desenhar(
                  painel,
                  'carteira',
                  visaoCarteira,
                  LARGURA_IMAGEM,
                  CORES_CLARO,
                  cabecalho(
                    visaoCarteira === 'corrida' ? `Corrida da carteira · ${mesCurto}` : `Carteira atendida · ${mesCurto}`,
                    `Clientes que compraram ${periodoDosDados(painel)} sobre a carteira de cada representante · equipe em ${formatarPorcentagem(painel.equipe.carteira)}`,
                    'carteira',
                  ),
                )
              }
              nomeImagem={`carteira-${sufixoArquivo}.png`}
            />
          ) : fonte === 'winthor' ? (
            <Aviso tipo="info">
              Para ver a carteira atendida por representante, importe a carteira de clientes do Winthor (um arquivo com todos os vendedores).{' '}
              <a href={linkPara({ tela: 'importar' })}>Importar</a>
            </Aviso>
          ) : null}

          {painel.foraDoRanking.length > 0 ? (
            <Aviso tipo="info">
              Fora da conta (sem meta): {painel.foraDoRanking.map((f) => `${f.representante.nome} ${formatarMoeda(f.liquido)}`).join('; ')}.
            </Aviso>
          ) : null}
        </div>
      ) : carregando ? (
        <p className="texto-apagado">Carregando…</p>
      ) : null}
    </div>
  );
}
